import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { apiFormatError, invalidatePendingSession, settlePendingRefresh } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let active = true;
    try { localStorage.removeItem("atelier_token"); }
    catch (error) { console.warn("Unable to remove legacy portal token", error.name); }
    const expired = () => setUser(null);
    window.addEventListener("atelier-session-expired", expired);
    api.get("/auth/me").then(({ data }) => { if (active) setUser(data); })
      .catch((error) => {
        if (error.response?.status !== 401) console.error("Session check failed", error.response?.status);
        if (active) setUser(null);
      }).finally(() => { if (active) setChecking(false); });
    return () => { active = false; window.removeEventListener("atelier-session-expired", expired); };
  }, []);

  const login = useCallback(async (email, password) => {
    invalidatePendingSession();
    setUser(null);
    await settlePendingRefresh();
    try {
      const { data } = await api.post("/auth/login", { email, password }, { skipRefresh: true });
      setUser(data.user);
      return { ok: true };
    } catch (error) {
      return { ok: false, error: apiFormatError(error.response?.data?.detail) };
    }
  }, []);

  const logout = useCallback(async () => {
    invalidatePendingSession();
    await settlePendingRefresh();
    try {
      await api.post("/auth/logout", null, { skipRefresh: true });
      setUser(null);
      return true;
    } catch (error) {
      console.error("Server sign-out failed", error.response?.status);
      return false;
    }
  }, []);

  const value = useMemo(() => ({ user, checking, login, logout }), [user, checking, login, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}